const API_BASE_URL = (import.meta.env && import.meta.env.VITE_API_URL) || 'https://api.greenguestbd.com/api';

class ApiService {
  private baseURL: string;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private getAuthHeaders(): HeadersInit {
    // Check for admin token first (for admin operations)
    // Then check for regular user token
    // dev_token is for developer panel operations only
    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('authToken');
    const token = adminToken || userToken;

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response, retryRequest?: () => Promise<Response>): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || `HTTP error! status: ${response.status}`;

      // Handle unauthorized/forbidden responses - try to refresh token
      if (response.status === 401 && retryRequest) {
        try {
          // Try to refresh the token
          const newToken = await this.refreshTokenIfNeeded();
          if (newToken) {
            // Retry the original request with new token
            const retryResponse = await retryRequest();
            if (retryResponse.ok) {
              return retryResponse.json();
            }
          }
        } catch (refreshError) {
          // If refresh fails, continue to error handling
        }
      }

      // Handle unauthorized/forbidden responses (token expired or invalid)
      if (response.status === 401 || response.status === 403) {
        // Check if the error message indicates authentication failure
        const isAuthError = errorMessage.toLowerCase().includes('authentication') ||
          errorMessage.toLowerCase().includes('unauthorized') ||
          errorMessage.toLowerCase().includes('token') ||
          errorMessage.toLowerCase().includes('login');

        if (isAuthError || errorMessage.toLowerCase().includes('session has expired')) {
          // Authentication error detected - clear tokens to force fresh login
          localStorage.removeItem('adminToken');
          localStorage.removeItem('authToken');
          localStorage.removeItem('admin');
          localStorage.removeItem('user');

          if (process.env.NODE_ENV === 'development') {
            console.warn('[ApiService] Session expired. Clearing tokens.');
          }
        }
      }

      // Create error with status code for better handling
      const error = new Error(errorMessage) as any;
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    return response.json();
  }

  private async refreshTokenIfNeeded(): Promise<string | null> {
    // If already refreshing, wait for that promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check if we have a token to refresh
    const token = localStorage.getItem('authToken') || localStorage.getItem('dev_token');
    if (!token) {
      return null;
    }
    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        // Use a direct fetch to avoid circular dependency with token refresh
        const refreshHeaders = this.getAuthHeaders();
        const refreshResponse = await fetch(`${this.baseURL}/auth/refresh`, {
          method: 'POST',
          headers: refreshHeaders,
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          if (refreshData.token) {
            localStorage.setItem('authToken', refreshData.token);
            return refreshData.token;
          }
        } else {
          await refreshResponse.json().catch(() => ({}));
        }
        return null;
      } catch (error) {
        // Token refresh error handled silently
        // Don't clear tokens immediately - let user manually logout
        return null;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async get<T>(endpoint: string): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = async (): Promise<Response> => {
      const headers = this.getAuthHeaders();

      return fetch(url, {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include',
      });
    };

    const response = await makeRequest();

    // Pass retry function for 401 errors
    return this.handleResponse<T>(response, async () => {
      const headers = this.getAuthHeaders();
      return fetch(url, {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include',
      });
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include',
    });
    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    return this.handleResponse<T>(response);
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include',
    });
    return this.handleResponse<T>(response);
  }
}

export const apiService = new ApiService();
export default apiService;
